import type { MonthlyChannelProfit } from "@/utils/accounting";
import CollapsibleBody from "@/components/base/CollapsibleBody";
import { formatCOP } from "@/utils/format";

const MonthlyProfitPanel = ({ months }: { months: MonthlyChannelProfit[] }) => {
  const totals = months.reduce(
    (acc, m) => ({
      direct: acc.direct + m.direct,
      advisor: acc.advisor + m.advisor,
      total: acc.total + m.total,
    }),
    { direct: 0, advisor: 0, total: 0 }
  );

  return (
    <div className="glass-panel rounded-lg p-4 md:p-5">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-8 h-8 rounded-md bg-accent-500/20 text-accent-200 flex items-center justify-center">
          <i className="ri-line-chart-line" />
        </span>
        <h3 className="editable-title text-base font-bold">Ganancia realizada mes a mes</h3>
      </div>
      <p className="text-[11px] text-foreground-500 mb-4">
        Compara cuánto ganas por <strong className="text-primary-200">punto / internet</strong> frente a
        la <strong className="text-secondary-200">red de asesores</strong> en cada mes.
      </p>

      {months.length === 0 ? (
        <p className="py-8 text-center text-xs text-foreground-500">
          Aún no hay ventas ni movimientos con fecha. Cuando registres ventas aparecerá el comparativo.
        </p>
      ) : (
        <>
          <CollapsibleBody count={months.length} threshold={5} unit="meses">
            <div className="space-y-3.5">
              {months.map((m) => {
                const monthTotal = Math.max(1, m.total);
                const directPct = (m.direct / monthTotal) * 100;
                const advisorPct = (m.advisor / monthTotal) * 100;
                return (
                  <div key={m.key} className="rounded-md bg-background-200/30 border border-background-300/40 px-3 py-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-foreground-800 capitalize flex items-center gap-1.5">
                        <i className="ri-calendar-line text-accent-300" /> {m.label}
                      </span>
                      <span className="text-sm font-heading font-extrabold text-foreground-950 whitespace-nowrap">
                        {formatCOP(m.total)}
                      </span>
                    </div>

                    <div className="h-2 rounded-full bg-background-300/40 overflow-hidden mt-2 flex">
                      <div
                        className="h-full bg-gradient-to-r from-primary-400 to-primary-500"
                        style={{ width: `${directPct}%` }}
                      />
                      <div
                        className="h-full bg-gradient-to-r from-secondary-400 to-secondary-500"
                        style={{ width: `${advisorPct}%` }}
                      />
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mt-1.5 text-[10px]">
                      <span className="text-foreground-600 whitespace-nowrap">
                        <i className="ri-store-2-line text-primary-200" /> Punto / internet ·{" "}
                        <span className="font-semibold text-foreground-800">{formatCOP(m.direct)}</span>
                      </span>
                      <span className="text-foreground-600 whitespace-nowrap">
                        <i className="ri-hand-heart-line text-secondary-200" /> Asesores ·{" "}
                        <span className="font-semibold text-foreground-800">{formatCOP(m.advisor)}</span>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CollapsibleBody>

          <div className="mt-4 rounded-md bg-background-200/40 border border-accent-500/20 px-3 py-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-foreground-600">
                Total ganancia realizada
              </span>
              <span className="text-base font-heading font-extrabold text-accent-200 whitespace-nowrap">
                {formatCOP(totals.total)}
              </span>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mt-2 text-[10px] text-foreground-500">
              <span className="whitespace-nowrap">
                Punto / internet · <span className="font-semibold text-foreground-800">{formatCOP(totals.direct)}</span>
              </span>
              <span className="whitespace-nowrap">
                Asesores · <span className="font-semibold text-foreground-800">{formatCOP(totals.advisor)}</span>
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MonthlyProfitPanel;